import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { QuestionRenderer, encodeFieldName } from './QuestionRenderer';
import { getSurveyForProfileType, submitRegistrationSurvey } from '@/api/registration';
import { useLocalization } from '@/contexts/LocalizationContext';
import { useProviderTypes } from '@/hooks/useProviderTypes';
import { getProviderTypeDisplay } from '@/utils/providerLocalization';
import type {
  SurveyDefinition,
  SurveyQuestion,
  RegistrationProfileType,
  SubmitRegistrationSurveyRequest,
} from '@/types/survey';
import { isSurveyQuestionVisible } from '@/utils/surveyFormHelpers';

interface SurveyFormRendererProps {
  /** The profile type to fetch the survey for */
  profileType: RegistrationProfileType;
  /** Optional: override the survey title */
  title?: string;
  /** Optional: override the survey description */
  description?: string;
  /**
   * When set, sent as `entity_id` on submit **only** for `profileType` `provider` or `provider_nh`.
   * Intended exclusively for `AddProviderDialog` on the Providers page.
   * Public login/register (`RegisterPage`, etc.) must leave this unset.
   */
  providersPageOrganizationEntityId?: string | null;
  /**
   * When set, fixes the provider affiliation code up-front (e.g. `partner`, `flex`,
   * `nonhealthcare`) and hides the provider-type picker. Only meaningful for provider flows.
   */
  defaultProviderType?: string;
  /** Callback after successful submission */
  onSuccess?: () => void;
}

type FormState = 'loading' | 'ready' | 'submitting' | 'success' | 'error' | 'no_survey' | 'select_type';

/**
 * Dynamic survey form renderer.
 * Fetches the survey definition from the backend and renders form fields dynamically.
 * Handles form validation, file uploads, and submission.
 */
export const SurveyFormRenderer: React.FC<SurveyFormRendererProps> = ({
  profileType,
  title: titleOverride,
  description: descriptionOverride,
  providersPageOrganizationEntityId,
  defaultProviderType,
  onSuccess,
}) => {
  const { locale, t } = useLocalization();
  const [formState, setFormState] = useState<FormState>('loading');
  const [survey, setSurvey] = useState<SurveyDefinition | null>(null);
  const [loadError, setLoadError] = useState<string>('');
  // Selected provider affiliation code; drives which survey/strategy is fetched (§3b) and is
  // sent on submit (§3c). Only meaningful for provider / provider_nh; empty = backend default.
  const [providerType, setProviderType] = useState<string>(defaultProviderType ?? '');

  const isAr = locale === 'ar';
  const isProviderFlow = profileType === 'provider' || profileType === 'provider_nh';
  const { providerTypes: providerTypeOptions, isLoading: providerTypesLoading } = useProviderTypes({
    enabled: isProviderFlow,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = useForm<Record<string, any>>({
    mode: 'onBlur',
  });

  // Sort questions by order
  const sortedQuestions = useMemo(() => {
    if (!survey?.questions) return [];
    return [...survey.questions].sort((a, b) => a.order - b.order);
  }, [survey?.questions]);

  // Group questions by category for section rendering
  const groupedQuestions = useMemo(() => {
    const groups: Array<{ category: string; questions: SurveyQuestion[] }> = [];
    let currentCategory = '';

    for (const question of sortedQuestions) {
      const category = question.category || 'general';
      if (category !== currentCategory) {
        groups.push({ category, questions: [question] });
        currentCategory = category;
      } else {
        groups[groups.length - 1].questions.push(question);
      }
    }

    return groups;
  }, [sortedQuestions]);

  const shouldShowQuestion = useCallback(
    (question: SurveyQuestion): boolean =>
      isSurveyQuestionVisible(question, (name) => form.watch(name)),
    [form],
  );

  // Fetch survey on mount
  useEffect(() => {
    let cancelled = false;

    const fetchSurvey = async () => {
      // Provider type is required for provider flows and determines which survey loads, so don't
      // fetch (or show a form) until one is chosen — avoids wiping answers on a later selection.
      if (isProviderFlow && !providerType.trim()) {
        setSurvey(null);
        form.reset();
        setFormState('select_type');
        return;
      }
      try {
        setFormState('loading');
        setLoadError('');
        // Clear any answers from a previously loaded (different-type) survey.
        form.reset();
        const surveyData = await getSurveyForProfileType(
          profileType,
          isProviderFlow ? providerType : undefined,
        );

        if (cancelled) return;

        if (surveyData.no_survey_configured) {
          setFormState('no_survey');
          return;
        }

        setSurvey(surveyData);
        setFormState('ready');
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err instanceof Error ? err.message : 'Failed to load form'
        );
        setFormState('error');
      }
    };

    fetchSurvey();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileType, providerType, isProviderFlow]);

  // Handle form submission
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (data: Record<string, any>) => {
    if (!survey) return;
    // Provider type is required for provider flows (the form is gated on it, but guard anyway).
    if (isProviderFlow && !providerType.trim()) {
      setFormState('select_type');
      return;
    }

    setFormState('submitting');

    try {
      // Build answers object - only include visible questions.
      // Field names are encoded (dots replaced) for react-hook-form compatibility,
      // so we read from the encoded key but submit using the original question code.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const answers: Record<string, any> = {};
      for (const question of sortedQuestions) {
        const encoded = encodeFieldName(question.code);
        if (shouldShowQuestion(question) && data[encoded] !== undefined) {
          answers[question.code] = data[encoded];
        }
      }

      const payload: SubmitRegistrationSurveyRequest = {
        survey_id: survey.id,
        profile_type: profileType,
        answers,
      };
      if (isProviderFlow) {
        const eid = providersPageOrganizationEntityId?.trim();
        if (eid) {
          payload.entity_id = eid;
        }
        const code = providerType.trim();
        if (code) {
          payload.provider_type = code;
        }
      }
      await submitRegistrationSurvey(payload);

      setFormState('success');
      toast.success(t('registration.successTitle'));
      onSuccess?.();
    } catch (err) {
      setFormState('ready');
      const errorMsg =
        err instanceof Error ? err.message : t('registration.errorMessage');
      toast.error(t('registration.errorTitle'), { description: errorMsg });
    }
  };

  const handleRetry = () => {
    setFormState('loading');
    getSurveyForProfileType(
      profileType,
      isProviderFlow ? providerType : undefined,
    ).then((surveyData) => {
      if (surveyData.no_survey_configured) {
        setFormState('no_survey');
      } else {
        setSurvey(surveyData);
        setFormState('ready');
      }
    }).catch((err) => {
      setLoadError(err instanceof Error ? err.message : 'Failed to load form');
      setFormState('error');
    });
  };

  // Determine title and description
  const surveyTitle = titleOverride || (isAr ? survey?.title_ar : survey?.title_en) || survey?.title || '';
  const surveyDescription =
    descriptionOverride || (isAr ? survey?.description_ar : survey?.description_en) || survey?.description || '';

  // Provider affiliation picker — drives which survey is fetched (§3b) and what is submitted (§3c).
  // Rendered for provider flows and kept mounted across loading/error/form states so changing the
  // selection re-fetches the matching survey in place. Empty = backend's entity-derived default.
  const providerTypePicker = isProviderFlow && !defaultProviderType ? (
    <Card>
      <CardContent className="p-4 sm:p-6 space-y-2">
        <label htmlFor="provider-type-select" className="block text-sm font-medium">
          {t('registration.providerTypeLabel')}
          <span className="text-destructive ms-1">*</span>
        </label>
        <select
          id="provider-type-select"
          value={providerType}
          onChange={(e) => setProviderType(e.target.value)}
          disabled={formState === 'submitting' || providerTypesLoading}
          className="w-full px-3 py-2 border border-input rounded-md bg-background disabled:opacity-50 text-sm"
        >
          <option value="">{t('registration.providerTypePlaceholder')}</option>
          {providerTypeOptions.map((opt) => (
            <option key={opt.code} value={opt.code}>
              {getProviderTypeDisplay(opt.code, opt.name, t)}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">{t('registration.providerTypeHelp')}</p>
      </CardContent>
    </Card>
  ) : null;

  const renderBody = () => {
    // Awaiting a required provider-type selection before the form can load.
    if (formState === 'select_type') {
      return (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground">{t('registration.providerTypePrompt')}</p>
          </CardContent>
        </Card>
      );
    }

    // Render loading skeleton
    if (formState === 'loading') {
      return (
        <Card>
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <p className="text-muted-foreground">{t('registration.loadingForm')}</p>
            </div>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      );
    }

    // Render error state
    if (formState === 'error') {
      return (
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
            <h3 className="text-lg font-semibold">{t('registration.errorTitle')}</h3>
            <p className="text-muted-foreground">{loadError || t('registration.errorMessage')}</p>
            <Button onClick={handleRetry} variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              {t('common.retry')}
            </Button>
          </CardContent>
        </Card>
      );
    }

    // Render no survey configured state
    if (formState === 'no_survey') {
      return (
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground" />
            <h3 className="text-lg font-semibold">{t('registration.noSurveyTitle')}</h3>
            <p className="text-muted-foreground">{t('registration.noSurveyMessage')}</p>
          </CardContent>
        </Card>
      );
    }

    // Render success state
    if (formState === 'success') {
      return (
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle className="h-16 w-16 mx-auto text-green-600" />
            <h3 className="text-2xl font-bold">{t('registration.successTitle')}</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              {t('registration.successMessage')}
            </p>
          </CardContent>
        </Card>
      );
    }

    // Render the form
    return (
      <Card className="shadow-lg">
        <CardContent className="p-4 sm:p-6 md:p-8">
          {/* Survey title and description */}
          {(surveyTitle || surveyDescription) && (
            <div className="mb-6 sm:mb-8 text-center">
              {surveyTitle && (
                <h2 className="text-xl sm:text-2xl font-bold mb-2">{surveyTitle}</h2>
              )}
              {surveyDescription && (
                <p className="text-sm sm:text-base text-muted-foreground">{surveyDescription}</p>
              )}
            </div>
          )}

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 sm:space-y-8">
            {groupedQuestions.map((group) => (
              <div key={group.category} className="space-y-6">
                {/* Category header */}
                {group.category !== 'general' && (
                  <div className="border-b pb-2">
                    <h3 className="text-lg font-semibold capitalize">
                      {group.category.replace(/_/g, ' ')}
                    </h3>
                  </div>
                )}

                {/* Questions in this group */}
                {group.questions.map((question) => {
                  if (!shouldShowQuestion(question)) return null;

                  return (
                    <QuestionRenderer
                      key={question.code}
                      question={question}
                      form={form}
                      locale={locale}
                      disabled={formState === 'submitting'}
                      snapshotId={survey?.snapshot_id ?? null}
                    />
                  );
                })}
              </div>
            ))}

            {/* Submit button */}
            <div className="flex justify-center pt-6">
              <Button
                type="submit"
                size="lg"
                disabled={formState === 'submitting'}
                className="min-w-[200px]"
              >
                {formState === 'submitting' ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    {t('common.submitting')}
                  </>
                ) : (
                  t('common.submit')
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {formState !== 'success' && providerTypePicker}
      {renderBody()}
    </div>
  );
};
